import React, { useState, useCallback, useContext } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  LinearProgress,
  Alert,
  Chip,
  Divider,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Card,
  CardContent,
  Grid,
  IconButton,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { useDropzone } from 'react-dropzone';
import {
  CloudUpload,
  Download,
  CheckCircle,
  Description,
  GetApp,
  Clear,
  Image,
  Group,
  FolderZip,
  Assessment,
} from '@mui/icons-material';
import { NotificationContext } from '../context/NotificationContext';
import { useProcess } from '../context/ProcessContext';
import { API_ENDPOINTS, apiCall } from '../config/api';
import FavoriteButton from '../components/FavoriteButton';
import { TOOL_COLORS, getCardBackground } from '../constants/colors';

const ItemImageGrouping = () => {
  const theme = useTheme();
  const { showNotification } = useContext(NotificationContext);
  const { addProcess, updateProcess, removeProcess } = useProcess();

  const [file, setFile] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const onDrop = useCallback((acceptedFiles) => {
    const validFiles = acceptedFiles.filter(file =>
      file.type === 'text/csv' ||
      file.type === 'application/vnd.ms-excel' ||
      file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
      file.name.toLowerCase().endsWith('.csv') ||
      file.name.toLowerCase().endsWith('.xlsx') ||
      file.name.toLowerCase().endsWith('.xls')
    );

    if (validFiles.length !== acceptedFiles.length) {
      setError('Only CSV and Excel files are supported');
    }

    if (validFiles.length === 0) return;

    setFile(validFiles[0]);
    setError('');
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx']
    },
    multiple: false
  });

  const removeFile = () => {
    setFile(null);
    setResults(null);
    setError('');
    setSuccess('');
  };

  const processFile = async () => {
    if (!file) {
      setError('Please select a file to process');
      return;
    }

    setProcessing(true);
    setError('');
    setSuccess('');

    const processId = addProcess({
      name: 'Item Image Grouping',
      status: 'running',
      startTime: Date.now()
    });

    try {
      const formData = new FormData();
      formData.append('file', file);

      const result = await apiCall(API_ENDPOINTS.ITEM_IMAGE_GROUPING_PROCESS, {
        method: 'POST',
        body: formData
      });

      setResults(result);
      setSuccess(`Successfully processed ${result.processedItems} items with ${result.totalImages} images!`);

      updateProcess(processId, { status: 'success' });
      setTimeout(() => removeProcess(processId), 3000);

    } catch (error) {
      setError(`Processing failed: ${error.message}`);
      updateProcess(processId, { status: 'error', error: error.message });
      setTimeout(() => removeProcess(processId), 5000);
    } finally {
      setProcessing(false);
    }
  };

  const downloadFile = async (downloadUrl, fileName) => {
    try {
      const encodedUrl = encodeURIComponent(downloadUrl);
      const response = await apiCall(API_ENDPOINTS.ITEM_IMAGE_GROUPING_DOWNLOAD(encodedUrl));

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      showNotification('File downloaded successfully', 'success');
    } catch (error) {
      showNotification('Download failed', 'error');
    }
  };

  const downloadTemplate = async () => {
    try {
      const response = await apiCall(API_ENDPOINTS.ITEM_IMAGE_GROUPING_TEMPLATE);

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = 'ItemImageGrouping_Template.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      showNotification('Template downloaded successfully', 'success');
    } catch (error) {
      showNotification('Template download failed', 'error');
    }
  };

  const resetForm = () => {
    setFile(null);
    setResults(null);
    setError('');
    setSuccess('');
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: theme.palette.background.default }}>
      <Box sx={{ p: 3, maxWidth: 1200, margin: '0 auto', position: 'relative' }}>
        <FavoriteButton interfaceName="Item Image Grouping" category="excel" />
        <Typography
          variant="h5"
          gutterBottom
          sx={{
            color: theme.palette.text.primary,
            textAlign: 'center',
            fontWeight: 600,
            letterSpacing: '0.5px',
            borderBottom: `2px solid ${theme.palette.divider}`,
            pb: 2,
            mb: 4
          }}
        >
          ITEM IMAGE GROUPING
        </Typography>

        <Grid container spacing={3}>
          {/* Main Processing Section */}
          <Grid item xs={12} md={8}>
            <Paper elevation={0} sx={{ p: 3, border: `1px solid ${theme.palette.divider}` }}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                Image Processing
              </Typography>

              {/* File Upload */}
              <Card sx={{ mb: 3, bgcolor: file ? getCardBackground(theme) : 'transparent' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Description sx={{ color: TOOL_COLORS.EXCEL.primary }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      Import File (CSV/Excel)
                    </Typography>
                    {file && (
                      <Tooltip title="File selected">
                        <CheckCircle color="success" />
                      </Tooltip>
                    )}
                  </Box>

                  {/* Drag and Drop Zone */}
                  <Box
                    {...getRootProps()}
                    sx={{
                      border: `2px dashed ${isDragActive ? TOOL_COLORS.EXCEL.primary : file ? '#4CAF50' : theme.palette.divider}`,
                      borderRadius: 2,
                      p: 3,
                      textAlign: 'center',
                      bgcolor: isDragActive ? TOOL_COLORS.EXCEL.light : file ? '#4CAF5010' : 'transparent',
                      transition: 'all 0.3s ease',
                      mb: 2,
                      cursor: 'pointer',
                      '&:hover': {
                        borderColor: file ? '#4CAF50' : TOOL_COLORS.EXCEL.primary,
                        bgcolor: file ? '#4CAF5015' : TOOL_COLORS.EXCEL.light,
                      },
                    }}
                  >
                    <input {...getInputProps()} />
                    {file ? (
                      <>
                        <CheckCircle sx={{ fontSize: 48, color: '#4CAF50', mb: 1 }} />
                        <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                          1 file selected
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Click to replace
                        </Typography>
                      </>
                    ) : (
                      <>
                        <CloudUpload sx={{ fontSize: 48, color: isDragActive ? TOOL_COLORS.EXCEL.primary : theme.palette.text.secondary, mb: 1 }} />
                        <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                          {isDragActive ? 'Drop file here' : 'Drag & drop file here or click to select'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          CSV or Excel file with ItemID and ForeignItemID columns
                        </Typography>
                      </>
                    )}
                  </Box>

                  {file && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mt: 1.5,
                        p: 1,
                        bgcolor: 'background.paper',
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 1
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                        <Description sx={{ color: TOOL_COLORS.EXCEL.primary, fontSize: 20 }} />
                        <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {file.name}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary', flexShrink: 0 }}>
                          ({formatFileSize(file.size)})
                        </Typography>
                      </Box>
                      <IconButton size="small" onClick={removeFile} sx={{ color: theme.palette.error.main }}>
                        <Clear fontSize="small" />
                      </IconButton>
                    </Box>
                  )}

                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 2 }}>
                    <Button
                      variant="outlined"
                      onClick={downloadTemplate}
                      startIcon={<GetApp sx={{ fontSize: '18px !important' }} />}
                      sx={{
                        borderColor: alpha(theme.palette.text.primary, 0.2),
                        color: alpha(theme.palette.text.primary, 0.7),
                        bgcolor: 'transparent',
                        textTransform: 'none',
                        fontWeight: 500,
                        px: 2.5,
                        py: 0.8,
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          borderColor: alpha(theme.palette.text.primary, 0.4),
                          color: theme.palette.text.primary,
                          bgcolor: alpha(theme.palette.text.primary, 0.05),
                        },
                        '&:active': {
                          transform: 'scale(0.98)',
                        },
                      }}
                    >
                      Download Template
                    </Button>
                  </Box>
                </CardContent>
              </Card>

              {/* Process Button */}
              <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                <Button
                  variant="contained"
                  onClick={processFile}
                  disabled={!file || processing}
                  sx={{
                    bgcolor: TOOL_COLORS.EXCEL.primary,
                    '&:hover': { bgcolor: TOOL_COLORS.EXCEL.hover },
                    px: 4
                  }}
                >
                  {processing ? 'Processing Images...' : 'Process Images'}
                </Button>

                {(file || results) && (
                  <Button
                    variant="outlined"
                    onClick={resetForm}
                    sx={{
                      borderColor: theme.palette.divider,
                      color: theme.palette.text.primary,
                      '&:hover': {
                        borderColor: theme.palette.text.secondary,
                        bgcolor: alpha(theme.palette.text.primary, 0.05)
                      }
                    }}
                  >
                    Clear
                  </Button>
                )}
              </Box>
            </Paper>
          </Grid>

          {/* Status & Information Section */}
          <Grid item xs={12} md={4}>
            <Paper elevation={0} sx={{ p: 3, border: `1px solid ${theme.palette.divider}` }}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                Status & Information
              </Typography>

              {/* Progress */}
              {processing && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Processing images and creating package...
                  </Typography>
                  <LinearProgress sx={{ '& .MuiLinearProgress-bar': { bgcolor: TOOL_COLORS.EXCEL.primary } }} />
                </Box>
              )}

              {/* Error Alert */}
              {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
                  {error}
                </Alert>
              )}

              {/* Success Alert */}
              {success && (
                <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
                  {success}
                </Alert>
              )}

              {/* Results Summary */}
              {results && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                    Processing Results:
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon>
                        <Assessment sx={{ color: TOOL_COLORS.EXCEL.primary }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={`${results.processedItems} Items Processed`}
                        secondary="Items successfully matched"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <Image sx={{ color: TOOL_COLORS.EXCEL.primary }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={`${results.totalImages} Images Found`}
                        secondary="Images matched and renamed"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <FolderZip sx={{ color: TOOL_COLORS.EXCEL.primary }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="ZIP Package Ready"
                        secondary="Images + Excel report included"
                      />
                    </ListItem>
                  </List>

                  {/* Download Button */}
                  <Button
                    variant="contained"
                    fullWidth
                    startIcon={<Download />}
                    onClick={() => downloadFile(results.downloadUrl, results.fileName)}
                    sx={{
                      bgcolor: '#10B981',
                      '&:hover': { bgcolor: '#059669' },
                      mt: 2
                    }}
                  >
                    Download Package
                  </Button>
                </Box>
              )}

              {/* Download Section */}
              {results && (
                <Box>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Package includes renamed images in "Images" folder and Excel report with mapping details.
                  </Alert>
                </Box>
              )}

              <Divider sx={{ my: 3 }} />

              {/* Expected File Format */}
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                Expected File Format:
              </Typography>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 2 }}>
                CSV/Excel file with columns:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 3 }}>
                <Chip
                  label="ItemID"
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.7rem', borderColor: TOOL_COLORS.EXCEL.primary, color: TOOL_COLORS.EXCEL.primary }}
                />
                <Chip
                  label="ForeignItemID"
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.7rem', borderColor: TOOL_COLORS.EXCEL.primary, color: TOOL_COLORS.EXCEL.primary }}
                />
              </Box>

              {/* Size Groups */}
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                Size Groups:
              </Typography>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.6, mb: 2 }}>
                <strong>Rectangle:</strong> 3, 4, 5, 6, 7, 8, 9<br />
                <strong>Round:</strong> 3R-12R<br />
                <strong>Runner:</strong> 26-29, 210-216, 218, 220, 222<br />
                <strong>Square:</strong> 3SQ-10SQ, 12SQ<br />
                <strong>Custom:</strong> 2, 24
              </Typography>

              {/* Instructions */}
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                Instructions:
              </Typography>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.6 }}>
                1. Download CSV template<br />
                2. Fill with ItemID and ForeignItemID<br />
                3. Upload populated file<br />
                4. Process to find and group images<br />
                5. Download ZIP package<br /><br />
                <strong>Note:</strong> System prioritizes exact matches, maximum 15 images per item
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default ItemImageGrouping;
